import React, { Component, useState } from 'react';
import { parseDashcamDeepLink } from './components/fleet/dashcamPairingUtils';
import {
  Activity,
  Building2,
  CircleDollarSign,
  DollarSign,
  FileText,
  Fuel,
  HardHat,
  Layers,
  LayoutDashboard,
  Radio,
  Scale,
  ShieldAlert,
  Smartphone,
  Terminal,
  Truck,
  UserCheck,
  Users,
  Wallet,
  Wrench,
  Zap,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import FleetManagement from './components/FleetManagement';
import DriverManagement from './components/DriverManagement';
import DriverMobileApp from './components/DriverMobileApp';
import WorkshopMaintenance from './components/WorkshopMaintenance';
import CustomerPortal from './components/CustomerPortal';
import VendorPortal from './components/VendorPortal';
import ComplianceFinance from './components/ComplianceFinance';
import TransportCompany from './components/TransportCompany';
import AvailabilityEngine from './components/AvailabilityEngine';
import AssetManagement from './components/AssetManagement';
import ContractManagement from './components/ContractManagement';
import DispatchControlTower from './components/DispatchControlTower';
import IncidentManagement from './components/IncidentManagement';
import FinanceCostingEngine from './components/FinanceCostingEngine';
import DriverPayroll from './components/DriverPayroll';
import TransportVendorMaster from './components/TransportVendorMaster';
import LiveDispatchMonitor from './components/LiveDispatchMonitor';
import FinancialSettlements from './components/FinancialSettlements';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import DashboardOverview from './components/DashboardOverview';
import PageShell from './components/layout/PageShell';
import LoginPage from './components/auth/LoginPage';
import { P } from './theme/logisticsTheme';
import { clearSession, readSession } from './utils/authSession';

const SIDEBAR = {
  white: P.white,
  grid: P.grid,
  silver: P.silver,
  main: P.silver,
  blue: P.blue,
  blueTint: P.blueTint,
  text: P.text,
  muted: P.muted,
  subtle: P.subtle,
};

const NAV_SECTIONS = [
  {
    title: 'Core Operations',
    items: [
      {
        id: 'analytics',
        label: 'Analytics & Dashboard Overview',
        icon: LayoutDashboard,
        description: 'Fleet KPIs · Dispatch trends · Margins',
        mobileLabel: 'Analytics',
        badge: 'Home',
      },
      {
        id: 'payroll',
        label: 'Driver Payroll Portal',
        icon: Wallet,
        description: 'Automated salary · Trip incentives',
        mobileLabel: 'Payroll',
      },
      {
        id: 'transport-vendors',
        label: 'Transport Vendor Master',
        icon: Fuel,
        description: 'Outsourced tankers · Fleet contracts',
        mobileLabel: 'Fuel Vendors',
        badge: 'Live',
      },
      {
        id: 'live-dispatch',
        label: 'Live Dispatch Monitor',
        icon: Radio,
        description: 'Assign orders · Vendor & driver matching',
        mobileLabel: 'Live Dispatch',
        badge: 'Live',
      },
      {
        id: 'financial-settlements',
        label: 'Financial Settlements',
        icon: CircleDollarSign,
        description: 'Freight reconciliation · Blending margins',
        mobileLabel: 'Settlements',
        badge: 'Finance',
      },
    ],
  },
  {
    title: 'Mission Control',
    items: [
      {
        id: 'dispatch',
        label: 'Dispatch Control Tower',
        icon: Terminal,
        description: 'Live trips · Alerts · Re-routing',
        mobileLabel: 'Dispatch',
        badge: 'Live',
      },
    ],
  },
  {
    title: 'ERP Modules',
    items: [
      {
        id: 'dashboard',
        label: 'Operations Dashboard',
        icon: LayoutDashboard,
        description: 'Fleet availability · Dispatch readiness',
        mobileLabel: 'Dashboard',
      },
      {
        id: 'fleet',
        label: 'Fleet Management',
        icon: Truck,
        description: 'GPS · Tankers · Geofencing',
        mobileLabel: 'Fleet',
        badge: 'Active',
      },
      {
        id: 'drivers',
        label: 'Driver Management',
        icon: HardHat,
        description: 'Profiles · Timesheets · Payroll',
        mobileLabel: 'Drivers',
      },
      {
        id: 'mobile',
        label: 'Driver Mobile Portal (Sim)',
        icon: Smartphone,
        description: 'Driver app · Pre-trip inspection',
        mobileLabel: 'Mobile Sim',
      },
      {
        id: 'transport',
        label: 'Transport Company Master',
        icon: Building2,
        description: 'Partners · Rate cards · Contracts',
        mobileLabel: 'Transport',
      },
      {
        id: 'availability',
        label: 'Truck Availability Engine',
        icon: Activity,
        description: 'Live status · Dispatch ready',
        mobileLabel: 'Availability',
        badge: 'Live',
      },
      {
        id: 'workshop',
        label: 'Workshop & Maintenance',
        icon: Wrench,
        description: 'Preventive · Breakdown response',
        mobileLabel: 'Workshop',
      },
      {
        id: 'customer',
        label: 'Customer Portal (Client View)',
        icon: UserCheck,
        description: 'Deliveries · Billing · Self-service',
        mobileLabel: 'Client',
      },
      {
        id: 'compliance',
        label: 'Compliance & Finance',
        icon: Scale,
        description: 'Salik · Customs · B2B billing',
        mobileLabel: 'Compliance',
      },
    ],
  },
  {
    title: 'Assets & Vendor Relations',
    items: [
      {
        id: 'vendor',
        label: 'Vendor Portal',
        icon: Users,
        description: 'Transporter jobs · Invoices · Availability',
        mobileLabel: 'Vendor',
      },
      {
        id: 'assets',
        label: 'Asset Management',
        icon: Layers,
        description: 'ISO · Flexi · Depot storage',
        mobileLabel: 'Assets',
      },
      {
        id: 'contracts',
        label: 'Contract Ledger',
        icon: FileText,
        description: 'Create · Renew · SLA alerts',
        mobileLabel: 'Contracts',
      },
    ],
  },
  {
    title: 'Safety & Finance',
    items: [
      {
        id: 'incidents',
        label: 'Incident & EHS Log',
        icon: ShieldAlert,
        description: 'Safety incidents · LTI · Create & close',
        mobileLabel: 'EHS Log',
        badge: 'EHS',
      },
      {
        id: 'finance',
        label: 'Finance & Costing',
        icon: DollarSign,
        description: 'Trip margins · KPIs · Receivables',
        mobileLabel: 'Finance',
      },
    ],
  },
];

const NAV_ITEMS = NAV_SECTIONS.flatMap((section) => section.items);

const isValidComponent = (ComponentType) =>
  typeof ComponentType === 'function' || (typeof ComponentType === 'object' && ComponentType !== null);

function ModuleLoadFallback({ title, message }) {
  return (
    <div
      className="rounded-2xl border p-10 text-center shadow-sm"
      style={{
        backgroundColor: SIDEBAR.white,
        borderColor: SIDEBAR.grid,
        color: SIDEBAR.text,
      }}
    >
      <p className="text-xs font-bold uppercase tracking-widest" style={{ color: SIDEBAR.blue }}>
        Module Guard
      </p>
      <h2 className="mt-2 text-lg font-extrabold">{title}</h2>
      <p className="mt-2 text-sm font-medium" style={{ color: SIDEBAR.muted }}>
        {message}
      </p>
    </div>
  );
}

class ModuleErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMessage: error?.message || 'Unknown runtime error',
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ModuleErrorBoundary]', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ModuleLoadFallback
          title={`${this.props.moduleName} Runtime Error`}
          message={this.state.errorMessage}
        />
      );
    }
    return this.props.children;
  }
}

function SafeModule({ name, component: ModuleComponent }) {
  if (!isValidComponent(ModuleComponent)) {
    return (
      <ModuleLoadFallback
        title={name}
        message={`${name} Component Loading Error — export not resolved.`}
      />
    );
  }
  return (
    <ModuleErrorBoundary moduleName={name}>
      <PageShell>
        <ModuleComponent />
      </PageShell>
    </ModuleErrorBoundary>
  );
}

function AppSidebar({
  currentView,
  onNavigate,
  session,
  onLogout,
  collapsed = false,
  onToggleCollapse,
  onClose,
  className = '',
}) {
  const handleNav = (viewId) => {
    onNavigate(viewId);
    onClose?.();
  };

  return (
    <aside
      className={`flex h-full flex-shrink-0 flex-col transition-[width] duration-200 ease-in-out ${
        collapsed ? 'w-[4.5rem]' : 'w-72'
      } ${className}`}
      style={{
        backgroundColor: SIDEBAR.white,
        borderRight: `1px solid ${SIDEBAR.grid}`,
        color: SIDEBAR.text,
        colorScheme: 'light',
      }}
    >
      <div
        className={`flex items-center border-b ${collapsed ? 'justify-center px-2 py-4' : 'justify-between px-5 py-5'}`}
        style={{ borderColor: SIDEBAR.grid }}
      >
        <div className={`flex items-center ${collapsed ? '' : 'gap-3'}`}>
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl shadow-sm"
            style={{ background: `linear-gradient(135deg, ${SIDEBAR.blue}, #2563EB)` }}
          >
            <Zap className="h-5 w-5 text-white" strokeWidth={1.75} />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-sm font-black tracking-tight" style={{ color: SIDEBAR.text }}>
                OGTS LogiCore<span style={{ color: SIDEBAR.blue }}>™</span>
              </p>
              <p
                className="text-[10px] font-bold uppercase tracking-widest"
                style={{ color: SIDEBAR.subtle }}
              >
                Logistics Command Center
              </p>
            </div>
          )}
        </div>
        {!collapsed && onClose && (
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-logi-muted transition hover:bg-logi-silver md:hidden"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 space-y-4 overflow-y-auto p-2 sm:p-3" aria-label="OGTS LogiCore modules">
        {NAV_SECTIONS.map((section) => (
          <div key={section.title}>
            {!collapsed ? (
              <p
                className="mb-2 px-3 text-[10px] font-bold uppercase tracking-widest"
                style={{ color: SIDEBAR.subtle }}
              >
                {section.title}
              </p>
            ) : (
              <div className="mx-2 mb-2 border-t" style={{ borderColor: SIDEBAR.grid }} />
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = currentView === item.id;

                return (
                  <button
                    key={item.id}
                    type="button"
                    title={collapsed ? item.label : undefined}
                    onClick={() => handleNav(item.id)}
                    className={`flex w-full rounded-lg text-left transition-colors ${
                      collapsed
                        ? 'items-center justify-center px-2 py-2.5'
                        : 'items-start gap-3 px-3 py-2.5'
                    }`}
                    style={{
                      backgroundColor: isActive ? SIDEBAR.blueTint : 'transparent',
                      color: isActive ? SIDEBAR.text : SIDEBAR.muted,
                      borderLeft: collapsed
                        ? 'none'
                        : isActive
                          ? `3px solid ${SIDEBAR.blue}`
                          : '3px solid transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.backgroundColor = SIDEBAR.silver;
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <div
                      className={`flex shrink-0 items-center justify-center rounded-lg ${
                        collapsed ? 'h-9 w-9' : 'mt-0.5 h-8 w-8'
                      }`}
                      style={{
                        backgroundColor: isActive ? SIDEBAR.white : SIDEBAR.silver,
                        boxShadow: isActive ? `inset 0 0 0 1px ${SIDEBAR.grid}` : 'none',
                        outline: collapsed && isActive ? `2px solid ${SIDEBAR.blue}` : 'none',
                      }}
                    >
                      <Icon
                        className="h-4 w-4"
                        style={{ color: isActive ? SIDEBAR.blue : SIDEBAR.subtle }}
                        strokeWidth={1.75}
                      />
                    </div>
                    {!collapsed && (
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="text-sm font-semibold leading-tight">{item.label}</p>
                          {item.badge && (
                            <span
                              className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide"
                              style={{
                                backgroundColor: '#ECFDF5',
                                color: '#059669',
                                boxShadow: 'inset 0 0 0 1px #A7F3D0',
                              }}
                            >
                              {item.badge}
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 text-[11px] leading-snug" style={{ color: SIDEBAR.subtle }}>
                          {item.description}
                        </p>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className={`space-y-2 border-t p-2 sm:p-4`} style={{ borderColor: SIDEBAR.grid }}>
        {session && !collapsed && (
          <div
            className="rounded-xl px-3 py-2.5"
            style={{ backgroundColor: SIDEBAR.silver, border: `1px solid ${SIDEBAR.grid}` }}
          >
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: SIDEBAR.subtle }}>
              Signed in as
            </p>
            <p className="truncate text-xs font-bold" style={{ color: SIDEBAR.text }}>
              {session.displayName || session.email}
            </p>
            <p className="truncate text-[11px]" style={{ color: SIDEBAR.muted }}>
              {session.email}
            </p>
          </div>
        )}
        <button
          type="button"
          onClick={onLogout}
          title="Sign Out"
          className={`flex items-center justify-center gap-2 rounded-lg border text-xs font-bold text-black transition hover:opacity-90 ${
            collapsed ? 'px-2 py-2' : 'w-full px-3 py-2'
          }`}
          style={{
            backgroundColor: P.btnBlue,
            borderColor: P.btnBlueBorder,
          }}
        >
          <LogOut className="h-3.5 w-3.5" />
          {!collapsed && 'Sign Out'}
        </button>
        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={`hidden items-center justify-center gap-2 rounded-lg border border-logi-grid bg-logi-silver text-xs font-semibold text-logi-muted transition hover:bg-white md:flex ${
              collapsed ? 'px-2 py-2' : 'w-full px-3 py-2'
            }`}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4" />
                Collapse menu
              </>
            )}
          </button>
        )}
        {!collapsed && (
          <div
            className="rounded-xl px-3 py-2.5"
            style={{ backgroundColor: SIDEBAR.silver, border: `1px solid ${SIDEBAR.grid}` }}
          >
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: SIDEBAR.subtle }}>
              Blueprint v1.0
            </p>
            <p className="text-xs font-semibold" style={{ color: SIDEBAR.muted }}>
              {NAV_ITEMS.length} modules · Phase 1 active
            </p>
          </div>
        )}
      </div>
    </aside>
  );
}

function MobileHeader({ currentView, onOpenMenu, session, onLogout }) {
  const activeItem = NAV_ITEMS.find((item) => item.id === currentView);

  return (
    <header
      className="flex items-center gap-2 border-b px-3 py-2.5 md:hidden"
      style={{ backgroundColor: SIDEBAR.white, borderColor: SIDEBAR.grid }}
    >
      <button
        type="button"
        onClick={onOpenMenu}
        className="inline-flex shrink-0 items-center justify-center rounded-lg border p-2 text-logi-text transition hover:bg-logi-silver"
        style={{ borderColor: SIDEBAR.grid }}
        aria-label="Open navigation menu"
      >
        <Menu className="h-5 w-5" />
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold" style={{ color: SIDEBAR.text }}>
          {activeItem?.label || 'OGTS LogiCore™'}
        </p>
        <p className="truncate text-[10px]" style={{ color: SIDEBAR.muted }}>
          {session?.displayName || session?.email}
        </p>
      </div>
      <button
        type="button"
        onClick={onLogout}
        className="inline-flex shrink-0 items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[10px] font-bold text-black"
        style={{ backgroundColor: P.btnBlue, borderColor: P.btnBlueBorder }}
      >
        <LogOut className="h-3 w-3" />
        Out
      </button>
    </header>
  );
}

function AppMainView({ currentView }) {
  switch (currentView) {
    case 'analytics':
      return <SafeModule name="Analytics & Dashboard Overview" component={AnalyticsDashboard} />;
    case 'dispatch':
      return <SafeModule name="Dispatch Control Tower" component={DispatchControlTower} />;
    case 'live-dispatch':
      return <SafeModule name="Live Dispatch Monitor" component={LiveDispatchMonitor} />;
    case 'financial-settlements':
      return <SafeModule name="Financial Settlements" component={FinancialSettlements} />;
    case 'dashboard':
      return <SafeModule name="Operations Dashboard" component={DashboardOverview} />;
    case 'fleet':
      return <SafeModule name="Fleet Management" component={FleetManagement} />;
    case 'drivers':
      return <SafeModule name="Driver Management" component={DriverManagement} />;
    case 'payroll':
      return <SafeModule name="Driver Payroll Portal" component={DriverPayroll} />;
    case 'mobile':
      return <SafeModule name="Driver Mobile Portal" component={DriverMobileApp} />;
    case 'transport':
      return <SafeModule name="Transport Company Master" component={TransportCompany} />;
    case 'transport-vendors':
      return <SafeModule name="Transport Vendor Master" component={TransportVendorMaster} />;
    case 'availability':
      return <SafeModule name="Truck Availability Engine" component={AvailabilityEngine} />;
    case 'workshop':
      return <SafeModule name="Workshop & Maintenance" component={WorkshopMaintenance} />;
    case 'customer':
      return <SafeModule name="Customer Portal" component={CustomerPortal} />;
    case 'vendor':
      return <SafeModule name="Vendor Portal" component={VendorPortal} />;
    case 'assets':
      return <SafeModule name="Asset Management" component={AssetManagement} />;
    case 'contracts':
      return <SafeModule name="Contract Ledger" component={ContractManagement} />;
    case 'incidents':
      return <SafeModule name="Incident & EHS Log" component={IncidentManagement} />;
    case 'finance':
      return <SafeModule name="Finance & Costing" component={FinanceCostingEngine} />;
    case 'compliance':
      return <SafeModule name="Compliance & Finance" component={ComplianceFinance} />;
    default:
      return <SafeModule name="Analytics & Dashboard Overview" component={AnalyticsDashboard} />;
  }
}

function App() {
  const [session, setSession] = useState(() => readSession());
  const [currentView, setCurrentView] = useState(() =>
    parseDashcamDeepLink().dash ? 'fleet' : 'analytics',
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleNavigate = (view) => {
    setCurrentView(view);
    setMobileMenuOpen(false);
  };

  const handleLoginSuccess = (nextSession) => {
    setSession(nextSession);
  };

  const handleLogout = () => {
    clearSession();
    setSession(null);
    setMobileMenuOpen(false);
  };

  if (!session) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div
      className="flex min-h-screen"
      style={{ backgroundColor: SIDEBAR.main, color: SIDEBAR.text, colorScheme: 'light' }}
    >
      <div className="hidden h-screen shrink-0 md:flex md:sticky md:top-0">
        <AppSidebar
          currentView={currentView}
          onNavigate={handleNavigate}
          session={session}
          onLogout={handleLogout}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
        />
      </div>

      {mobileMenuOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/40 md:hidden"
            aria-label="Close navigation menu"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 flex md:hidden">
            <AppSidebar
              currentView={currentView}
              onNavigate={handleNavigate}
              session={session}
              onLogout={handleLogout}
              onClose={() => setMobileMenuOpen(false)}
              className="h-full shadow-xl"
            />
          </div>
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <MobileHeader
          currentView={currentView}
          onOpenMenu={() => setMobileMenuOpen(true)}
          session={session}
          onLogout={handleLogout}
        />

        <main
          className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8"
          style={{ backgroundColor: SIDEBAR.main }}
        >
          <div key={currentView} className="mx-auto max-w-[1600px] animate-[fadeSlideIn_0.3s_ease-out]">
            <AppMainView currentView={currentView} />
          </div>
          <footer
            className="mx-auto mt-8 max-w-[1600px] border-t pt-6 text-center text-xs"
            style={{ borderColor: SIDEBAR.grid, color: SIDEBAR.subtle }}
          >
            OGTS LogiCore™ ERP · UAE Oil &amp; Gas Logistics
          </footer>
        </main>
      </div>
    </div>
  );
}

export default App;
